import SalaryCard from "../components/hris/SalaryCard";
import SalaryShare from "../components/hris/SalaryShare";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useCallback, useEffect, useState } from "react";
import { api, API, idr } from "../lib/api";
import { Wallet, TrendingUp, TrendingDown, Users, Download, Lock, Send, FileText, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useCabangOptions } from "../lib/cabang";
import { MonthPicker } from "@/components/ui/date-picker";
import { bulanIniISO, hariIniISO, formatTanggal } from "@/lib/tanggal";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

export default function RekapPayroll() {
  const { user, can } = useAuth();
  const [selectedSlip, setSelectedSlip] = useState(null);
  const BRANCHES = useCabangOptions({ denganKosong: true });
  const canClose = ["superadmin", "admin"].includes(user?.role);
  const [periode, setPeriode] = useState(bulanIniISO());
  const [branch, setBranch] = useState("");
  const [data, setData] = useState(null);
  const [closing, setClosing] = useState(false);
  const canConfirm = canClose && can("payroll", "create");
  const [paymentSlips, setPaymentSlips] = useState([]);
  const [paymentDate, setPaymentDate] = useState(hariIniISO());
  const [confirming, setConfirming] = useState(false);
  // Urutan bawaan (per karyawan) dipertahankan sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState();

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (periode) params.set("periode", periode);
    if (branch) params.set("branch", branch);
    return api.get(`/payroll/rekap?${params}`).then(r => {
      setData(r.data);
      setSelectedSlip(prev => prev ? r.data.slips.find(s => s.id === prev.id) || null : null);
    });
  }, [periode, branch]);
  useEffect(() => { load(); }, [load]);

  const openPayment = slips => {
    setPaymentDate(hariIniISO());
    setPaymentSlips(slips);
  };
  const confirmPayment = async () => {
    setConfirming(true);
    try {
      const { data: result } = await api.post("/payroll/payments/confirm", {
        slip_ids: paymentSlips.map(s => s.id), payment_date: paymentDate,
      });
      toast.success(`${result.confirmed.length} slip dikonfirmasi sudah dibayar${result.already_paid.length ? `; ${result.already_paid.length} sebelumnya sudah dikonfirmasi` : ""}.`);
      setPaymentSlips([]);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengonfirmasi pembayaran"); }
    finally { setConfirming(false); }
  };
  const awaitingPayment = data?.slips?.filter(s => s.closed_at && s.payment_status !== "paid") || [];

  const closePayroll = async () => {
    if (!confirm(`Close payroll periode ${periode}${branch ? " (" + branch + ")" : ""}? Setiap karyawan akan menerima slip gaji via WhatsApp.`)) return;
    setClosing(true);
    try {
      const { data: r } = await api.post("/payroll/close", { periode, branch: branch || null });
      const configured = r.twilio_configured;
      toast.success(`${r.closed} slip closed. WA: ${r.sent} terkirim, ${r.no_phone} tanpa nomor, ${r.failed} gagal${!configured ? " (Twilio belum aktif — MOCKED)" : ""}`, { duration: 8000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    setClosing(false);
  };

  const downloadPdf = async (slipId, nama) => {
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/payroll/slip/${slipId}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `SlipGaji_${nama}_${periode}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <Dialog open={paymentSlips.length > 0} onOpenChange={open => { if (!open && !confirming) setPaymentSlips([]); }}>
        <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>Konfirmasi sudah dibayar</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Gunakan setelah uang benar-benar diterima karyawan. Pembayaran menggunakan jurnal payroll yang sudah tercatat; saldo tidak dipotong ulang.</p>
          <ul className="space-y-2 text-sm" data-testid="payment-confirm-list">{paymentSlips.map(s => <li key={s.id} className="flex justify-between gap-3"><span>{s.employee_nama}</span><strong className="whitespace-nowrap">{idr(s.calc.thp)}</strong></li>)}</ul>
          <p className="text-sm font-semibold">Total {idr(paymentSlips.reduce((n, s) => n + s.calc.thp, 0))}</p>
          <label className="text-sm">Tanggal pembayaran
            <input type="date" value={paymentDate} max={hariIniISO()} onChange={e => setPaymentDate(e.target.value)} data-testid="payment-date" className="mt-2 w-full rounded-lg border p-3" />
          </label>
          <button onClick={confirmPayment} disabled={confirming || !paymentDate} data-testid="btn-confirm-paid" className="rounded-lg bg-emerald-600 text-white px-4 py-3 font-semibold disabled:opacity-50">{confirming ? "Menyimpan..." : "Ya, sudah dibayar"}</button>
        </DialogContent>
      </Dialog>
      <Dialog open={!!selectedSlip} onOpenChange={open => { if (!open) setSelectedSlip(null); }}>
        <DialogContent className="max-w-[610px] max-h-[92dvh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader><DialogTitle>Slip gaji digital</DialogTitle></DialogHeader>
          {selectedSlip && <><SalaryCard slip={selectedSlip} onDownload={() => downloadPdf(selectedSlip.id, selectedSlip.employee_nama)} />{can("payroll", "create") && <SalaryShare key={selectedSlip.id} slipId={selectedSlip.id} />}</>}
        </DialogContent>
      </Dialog>
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Payroll</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Rekap Payroll Bulanan</h1>
        <p className="mt-2 text-sm text-slate-500">Ringkasan seluruh slip gaji per periode dan cabang.</p>
      </header>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-6 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Periode</span>
          <MonthPicker value={periode} onChange={setPeriode}
            data-testid="rekap-periode"
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Branch</span>
          <select value={branch} onChange={e => setBranch(e.target.value)}
            data-testid="rekap-branch"
            className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[180px]">
            {BRANCHES.map(b => <option key={b} value={b}>{b || "Semua Branch"}</option>)}
          </select>
        </label>
        <button data-testid="btn-export-excel"
          onClick={async () => {
            const params = new URLSearchParams();
            if (periode) params.set("periode", periode);
            if (branch) params.set("branch", branch);
            const token = localStorage.getItem("hris_token");
            const res = await fetch(`${API}/payroll/rekap/export?${params}`, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `Rekap_Payroll_${periode || "all"}${branch ? "_" + branch : ""}.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
          }}
          className="ml-auto inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors">
          <Download className="w-4 h-4" /> Download Excel
        </button>
        {canClose && data && data.count > 0 && (
          <button data-testid="btn-close-payroll" onClick={closePayroll} disabled={closing}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#25D366] hover:bg-[#128C7E] text-white text-sm font-medium transition-colors disabled:opacity-60">
            <Send className="w-4 h-4" /> {closing ? "Mengirim..." : "Close & Kirim Slip"}
          </button>
        )}
        {canConfirm && awaitingPayment.length > 0 && <button onClick={() => openPayment(awaitingPayment)} disabled={closing} data-testid="btn-confirm-all-paid" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-700 text-white text-sm font-medium"><CheckCircle2 className="w-4 h-4" />Konfirmasi dibayar ({awaitingPayment.length})</button>}
      </div>
      {canConfirm && <p className="mb-5 text-sm text-slate-500">Finalisasi melalui <strong>Close & Kirim Slip</strong> mencatat pengeluaran payroll dan mencoba mengirim WhatsApp. Setelah transfer selesai, pilih <strong>Konfirmasi dibayar</strong> lalu isi tanggal pembayaran.</p>}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-6">
            <Kpi icon={Users} label="Slip Diterbitkan" value={data.count} tint="emerald" delay={0} />
            <Kpi icon={TrendingUp} label="Total Bruto" value={idr(data.total_bruto)} tint="sky" delay={0.05} />
            <Kpi icon={TrendingDown} label="Total Potongan" value={idr(data.total_potongan)} tint="rose" delay={0.1} />
            <Kpi icon={Wallet} label="Total THP" value={idr(data.total_thp)} tint="amber" delay={0.15} />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <SortTh label="NIK" sortKeyName="employee_nik" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="rekap-sort-nik" />
                  <SortTh label="Nama" sortKeyName="employee_nama" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="rekap-sort-nama" />
                  <SortTh label="Branch" sortKeyName="branch" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="rekap-sort-branch" />
                  <SortTh label="Grade" sortKeyName="grade_jabatan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="rekap-sort-grade" />
                  <SortTh label="Bruto" sortKeyName="bruto" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="rekap-sort-bruto" />
                  <SortTh label="Potongan" sortKeyName="potongan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="rekap-sort-potongan" />
                  <SortTh label="THP" sortKeyName="thp" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="rekap-sort-thp" />
                  <th className="text-center py-3 px-4">Status</th>
                  <th className="text-right py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {sortRows(data.slips, sortKey, sortDir, {
                  bruto: (s) => s.calc.total_bruto, potongan: (s) => s.calc.total_potongan, thp: (s) => s.calc.thp,
                }).map(s => (
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4 font-mono text-xs text-slate-600">{s.employee_nik}</td>
                    <td className="py-3 px-4 font-medium text-slate-900">{s.employee_nama}</td>
                    <td className="py-3 px-4 text-slate-600">{s.branch}</td>
                    <td className="py-3 px-4 text-slate-500 text-xs">{s.grade_jabatan}</td>
                    <td className="py-3 px-4 text-right font-mono text-xs">{idr(s.calc.total_bruto)}</td>
                    <td className="py-3 px-4 text-right font-mono text-xs text-rose-700">{idr(s.calc.total_potongan)}</td>
                    <td className="py-3 px-4 text-right font-mono text-xs font-semibold text-emerald-700">{idr(s.calc.thp)}</td>
                    <td className="py-3 px-4 text-center">
                      {s.closed_at ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800"><Lock className="w-3 h-3" /> Closed</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">Draft</span>
                      )}
                      <div className={`mt-1 text-xs ${s.payment_status === "paid" ? "text-emerald-700" : "text-amber-700"}`} data-testid={`payment-status-${s.id}`}>{s.payment_status === "paid" ? `Sudah dibayar${s.payment_date ? ` · ${formatTanggal(s.payment_date)}` : ""}` : "Belum dikonfirmasi dibayar"}</div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button className="text-xs px-3 py-2 rounded-lg border border-slate-200 mr-1" onClick={() => setSelectedSlip(s)} data-testid={`btn-digital-${s.id}`}>Lihat / Tautan</button>
                      {canConfirm && s.closed_at && s.payment_status !== "paid" && <button onClick={() => openPayment([s])} data-testid={`btn-paid-${s.id}`} className="text-xs px-3 py-2 rounded-lg border border-emerald-300 text-emerald-700 mr-1">Konfirmasi dibayar</button>}
                      <button onClick={() => downloadPdf(s.id, s.employee_nama)} data-testid={`btn-pdf-${s.id}`}
                        title="Download PDF"
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-900 transition-colors">
                        <FileText className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {data.slips.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-12 text-sm text-slate-500">
                    Belum ada slip gaji untuk periode ini. Buat slip di menu <span className="font-medium">Payroll / Slip Gaji</span>.
                  </td></tr>
                )}
              </tbody>
              {data.slips.length > 0 && (
                <tfoot>
                  <tr className="bg-slate-50 font-semibold text-slate-900">
                    <td colSpan={4} className="py-3 px-4">Total {data.count} slip</td>
                    <td className="py-3 px-4 text-right font-mono">{idr(data.total_bruto)}</td>
                    <td className="py-3 px-4 text-right font-mono text-rose-700">{idr(data.total_potongan)}</td>
                    <td className="py-3 px-4 text-right font-mono text-emerald-700" data-testid="rekap-total-thp">{idr(data.total_thp)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tint = "emerald", delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <div className={`w-10 h-10 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-4`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className="font-display text-xl font-bold text-slate-900">{value}</p>
    </motion.div>
  );
}
